import type {
  OpenWorldResponseSummary,
  OpenWorldResponseEvidence,
} from '@/lib/openWorlds/responses'
import { isOpenWorldJournalJson } from '@/lib/openWorlds/journalPersistence'
import { supabase } from '@/lib/supabaseClient'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import { mapOpenWorldPreparation } from '@/api/adapters/supabase/open-world-preparations'
import { mapStorySnapshot } from '@/api/adapters/supabase/open-world-journal'
import { OpenWorldPersistenceError } from '@/lib/openWorlds/persistence'
import { isOpenWorldAttemptUuid } from '@/lib/openWorlds/attemptPersistence'
import { isOpenWorldJournalRecord } from '@/lib/openWorlds/journalPersistence'
import { validOpenWorldConversationSettings } from '@/lib/openWorlds/conversation'
import type {
  BeginOpenWorldResponseRequest,
  BeginOpenWorldResponseResult,
  FinishOpenWorldResponseRequest,
  OpenWorldPendingResponse,
  OpenWorldResponseResult,
  SelectOpenWorldResponseRequest,
} from '@/lib/openWorlds/responses'

import { mapPendingOpenWorldResponse } from '@/api/mappers/openWorldResponses'
const fail = (): never => {
  throw new OpenWorldPersistenceError('backend', 'Malformed response alternative.')
}
async function rpc(
  name: string,
  parameters: Record<string, unknown>
): Promise<Record<string, unknown>> {
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc(name, parameters)
  if (error) {
    const code = /OW_(UNAUTHENTICATED|NOT_FOUND|CONFLICT|INVALID_INPUT)/
      .exec(error.message)?.[1]
      ?.toLowerCase()
    throw new OpenWorldPersistenceError(
      (code ?? 'backend') as
        | 'conflict'
        | 'backend'
        | 'unauthenticated'
        | 'not_found'
        | 'invalid_input',
      error.message
    )
  }
  if (!isOpenWorldJournalRecord(data)) fail()
  return data
}
function ids(...values: string[]) {
  if (!values.every(isOpenWorldAttemptUuid))
    throw new OpenWorldPersistenceError('invalid_input', 'Response identities must use UUIDs.')
}
function revision(value: number) {
  if (!Number.isInteger(value) || value < 1 || value > 2147483647)
    throw new OpenWorldPersistenceError('invalid_input', 'Response revision is invalid.')
}
const result = (value: Record<string, unknown>): OpenWorldResponseResult => {
  if (typeof value.replayed !== 'boolean') fail()
  return { snapshot: mapStorySnapshot(value.snapshot), replayed: value.replayed as boolean }
}
export async function beginOpenWorldResponse(
  request: BeginOpenWorldResponseRequest
): Promise<BeginOpenWorldResponseResult> {
  ids(request.id, request.attemptId, request.playthroughId, request.executionToken)
  revision(request.expectedPlaythroughRevision)
  const value = await rpc('rpc_begin_open_world_response_v1', {
    p_id: request.id,
    p_playthrough_id: request.playthroughId,
    p_attempt_id: request.attemptId,
    p_execution_token: request.executionToken,
    p_expected_revision: request.expectedPlaythroughRevision,
    p_legacy_context: request.legacyContext,
  })
  if (
    typeof value.replayed !== 'boolean' ||
    !Number.isInteger(value.botId) ||
    (value.botId as number) < 1 ||
    !validOpenWorldConversationSettings(value.conversationSettings)
  )
    fail()
  return {
    preparation: mapOpenWorldPreparation(value.preparation),
    botId: value.botId as number,
    conversationSettings:
      value.conversationSettings as BeginOpenWorldResponseResult['conversationSettings'],
    pendingResponse: mapPendingOpenWorldResponse(value.pendingResponse),
    replayed: value.replayed as boolean,
  }
}
export async function renewOpenWorldResponse(request: {
  id: string
  executionToken: string
  partialText: string
}): Promise<OpenWorldPendingResponse> {
  ids(request.id, request.executionToken)
  return mapPendingOpenWorldResponse(
    await rpc('rpc_renew_open_world_response_v1', {
      p_id: request.id,
      p_execution_token: request.executionToken,
      p_partial_text: request.partialText,
    })
  )
}
export async function finishOpenWorldResponse(
  request: FinishOpenWorldResponseRequest
): Promise<OpenWorldResponseResult> {
  ids(request.id, request.executionToken, request.receiptId)
  return result(
    await rpc('rpc_finish_open_world_response_v1', {
      p_id: request.id,
      p_execution_token: request.executionToken,
      p_receipt_id: request.receiptId,
      p_status: request.status,
      p_content: request.content,
      p_error: request.error,
      p_evidence: request.evidence,
    })
  )
}
export async function selectOpenWorldResponse(
  request: SelectOpenWorldResponseRequest
): Promise<OpenWorldResponseResult> {
  ids(request.playthroughId, request.attemptId, request.variantId, request.receiptId)
  revision(request.expectedPlaythroughRevision)
  return result(
    await rpc('rpc_select_open_world_response_v1', {
      p_playthrough_id: request.playthroughId,
      p_attempt_id: request.attemptId,
      p_variant_id: request.variantId,
      p_receipt_id: request.receiptId,
      p_expected_revision: request.expectedPlaythroughRevision,
    })
  )
}
export async function recoverOpenWorldResponse(id: string): Promise<OpenWorldResponseResult> {
  ids(id)
  return result(await rpc('rpc_recover_open_world_response_v1', { p_id: id }))
}

function mapResponseSummary(row: Record<string, unknown>): OpenWorldResponseSummary {
  if (
    !isOpenWorldAttemptUuid(row.id) ||
    !['running', 'completed', 'failed', 'cancelled', 'interrupted'].includes(
      row.status as string
    ) ||
    typeof row.created_at !== 'string' ||
    !Number.isFinite(Date.parse(row.created_at)) ||
    (row.finished_at !== null &&
      (typeof row.finished_at !== 'string' || !Number.isFinite(Date.parse(row.finished_at))))
  )
    fail()
  return {
    id: row.id as string,
    status: row.status as OpenWorldResponseSummary['status'],
    createdAt: row.created_at as string,
    finishedAt: row.finished_at as string | null,
  }
}
export async function listOpenWorldResponses(
  attemptId: string
): Promise<OpenWorldResponseSummary[]> {
  ids(attemptId)
  await requireOpenWorldSession()
  const { data, error } = await supabase
    .from('open_world_response_variants')
    .select('id,status,created_at,finished_at')
    .eq('attempt_id', attemptId)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true })
  if (error) throw new OpenWorldPersistenceError('backend', error.message)
  if (!Array.isArray(data)) fail()
  return (data as Record<string, unknown>[]).map(mapResponseSummary)
}
export async function getOpenWorldResponseEvidence(
  id: string
): Promise<OpenWorldResponseEvidence | null> {
  ids(id)
  await requireOpenWorldSession()
  const { data, error } = await supabase
    .from('open_world_response_variants')
    .select(
      'id,attempt_id,bot_id,source_variant_id,status,content,partial_text,error,evidence,created_at,finished_at'
    )
    .eq('id', id)
    .maybeSingle()
  if (error) throw new OpenWorldPersistenceError('backend', error.message)
  if (data === null) return null
  if (
    !isOpenWorldJournalRecord(data) ||
    !isOpenWorldJournalJson(data.evidence) ||
    !isOpenWorldAttemptUuid(data.attempt_id) ||
    !Number.isInteger(data.bot_id) ||
    (data.source_variant_id !== null && !isOpenWorldAttemptUuid(data.source_variant_id)) ||
    typeof data.partial_text !== 'string' ||
    (data.content !== null && typeof data.content !== 'string') ||
    (data.error !== null && typeof data.error !== 'string')
  )
    fail()
  const row = data as Record<string, unknown>
  return {
    ...mapResponseSummary(row),
    attemptId: row.attempt_id as string,
    botId: row.bot_id as number,
    sourceVariantId: row.source_variant_id as string | null,
    content: row.content as string | null,
    partialText: row.partial_text as string,
    error: row.error as string | null,
    evidence: structuredClone(row.evidence) as OpenWorldResponseEvidence['evidence'],
  }
}
